import type Immutable from 'immutable'
import type { Country } from '../AppData/_stores/AppDataStore'
import type UserCartStore from '../UserData/_stores/UserCartStore'
import { getSupportedPhoneCountryCode, phoneNumberIsPossible } from '../Common/Form/PhoneUtils'

type CheckoutAddressType = 'shipping' | 'billing'
export type CheckoutAddressValidationStatus = 'valid' | 'empty' | 'invalid'

const normalizeAddressField = (value: string) => value.trim().toLowerCase()

const hasText = (value: string) => normalizeAddressField(value) !== ''

const findCountryByName = (
  countries: Immutable.OrderedMap<string, Country>,
  countryName: string
) => {
  const normalizedCountryName = normalizeAddressField(countryName)

  if (normalizedCountryName === '') {
    return undefined
  }

  return countries.find(country => normalizeAddressField(country.get('name')) === normalizedCountryName)
}

const provinceIsValid = (country: Country, province: string) => {
  const normalizedProvince = normalizeAddressField(province)

  if (normalizedProvince === '') {
    return false
  }

  const provinces = country.get('provinces')
  if (provinces.count() === 0) {
    return true
  }

  return !!provinces.find(province => normalizeAddressField(province.get('name')) === normalizedProvince)
}

const postalCodeIsValid = (country: Country, postalCode: string) => {
  if (country.get('name') === 'Canada') {
    return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCode.trim())
  }

  if (country.get('name') === 'United States') {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(postalCode.trim())
  }

  return true
}

const phoneIsValid = (country: Country, phone: string) => {
  const normalizedPhone = phone.trim()
  const isInternational = !['CA', 'US'].includes(country.get('alpha2_code'))
  const countryCode = getSupportedPhoneCountryCode(country.get('alpha2_code'))

  if (normalizedPhone === '') {
    return !isInternational
  }

  return phoneNumberIsPossible(normalizedPhone, countryCode)
}

const cartHasCheckoutAddressContent = (
  cart: UserCartStore,
  type: CheckoutAddressType
) => {
  if (type === 'shipping') {
    return hasText(cart.get('name')) ||
      hasText(cart.get('address')) ||
      hasText(cart.get('address2')) ||
      hasText(cart.get('city')) ||
      hasText(cart.get('province')) ||
      hasText(cart.get('postalCode')) ||
      hasText(cart.get('country')) ||
      hasText(cart.get('shipping_phone'))
  }

  return hasText(cart.get('billing_name')) ||
    hasText(cart.get('billing_address')) ||
    hasText(cart.get('billing_address2')) ||
    hasText(cart.get('billing_city')) ||
    hasText(cart.get('billing_province')) ||
    hasText(cart.get('billing_postalCode')) ||
    hasText(cart.get('billing_country')) ||
    hasText(cart.get('billing_phone'))
}

export const cartHasValidCheckoutAddress = (
  cart: UserCartStore,
  type: CheckoutAddressType,
  countries: Immutable.OrderedMap<string, Country>
) => {
  if (type === 'shipping' && cart.get('pickup')) {
    return true
  }

  const country = findCountryByName(
    countries,
    type === 'shipping' ? cart.get('country') : cart.get('billing_country')
  )

  if (country === undefined) {
    return false
  }

  if (type === 'shipping') {
    return hasText(cart.get('name')) &&
      hasText(cart.get('address')) &&
      hasText(cart.get('city')) &&
      provinceIsValid(country, cart.get('province')) &&
      postalCodeIsValid(country, cart.get('postalCode')) &&
      phoneIsValid(country, cart.get('shipping_phone'))
  }

  return hasText(cart.get('billing_name')) &&
    hasText(cart.get('billing_address')) &&
    hasText(cart.get('billing_city')) &&
    provinceIsValid(country, cart.get('billing_province')) &&
    postalCodeIsValid(country, cart.get('billing_postalCode')) &&
    phoneIsValid(country, cart.get('billing_phone'))
}

export const getCartCheckoutAddressValidationStatus = (
  cart: UserCartStore,
  type: CheckoutAddressType,
  countries: Immutable.OrderedMap<string, Country>
): CheckoutAddressValidationStatus => {
  if (type === 'shipping' && cart.get('pickup')) {
    return 'valid'
  }

  if (!cartHasCheckoutAddressContent(cart, type)) {
    return 'empty'
  }

  return cartHasValidCheckoutAddress(cart, type, countries) ? 'valid' : 'invalid'
}
